import { useState } from "react";
import type { AnswerResult } from "../../../app/practice";
import { MAX_LEVEL, type ExerciseKind, type Question } from "../../../learning/exercises";
import { STAIRCASE_RULE } from "../../../learning/staircase";
import { usePractice, useServices } from "../../context";
import { Panel, Toggle } from "../controls";

const TITLES: Record<ExerciseKind, string> = { interval: "Intervals", chord: "Chord qualities" };
const PROMPTS: Record<ExerciseKind, string> = {
  interval: "Which interval did you hear?",
  chord: "Which kind of chord did you hear?",
};

/** Ear-training exercises with adaptive difficulty (F-LEARN-05/06). */
export function EarTraining() {
  const { practice } = useServices();
  const state = usePractice();
  const [kind, setKind] = useState<ExerciseKind>("interval");
  const [question, setQuestion] = useState<Question | null>(null);
  const [result, setResult] = useState<(AnswerResult & { given: string }) | null>(null);
  const stair = state.staircase[kind];
  const choices = practice.choices(kind);
  const awaiting = question !== null && result === null;

  const ask = () => { setResult(null); setQuestion(practice.ask(kind)); };
  const switchKind = (k: ExerciseKind) => { setKind(k); setQuestion(null); setResult(null); };
  const answer = async (id: string) => {
    const r = await practice.answer(id);
    if (r) setResult({ ...r, given: id });
  };

  return (
    <Panel title="Ear training" className="span-all">
      <div className="controls-row">
        <div className="segmented" role="group" aria-label="Exercise">
          {(["interval", "chord"] as ExerciseKind[]).map((k) => (
            <Toggle key={k} pressed={kind === k} onChange={() => switchKind(k)}>{TITLES[k]}</Toggle>
          ))}
        </div>
        <span className="status" aria-live="polite">Level {stair.level} of {MAX_LEVEL}</span>
        <span className="hint">{STAIRCASE_RULE}</span>
      </div>

      <div className="ear-stage">
        <p className="ear-prompt">{question ? PROMPTS[kind] : `Press Start to hear your first ${kind === "interval" ? "interval" : "chord"}.`}</p>
        <div className="controls-row">
          {!question || result ? (
            <button type="button" className="primary" onClick={ask}>{question ? "Next" : "Start"}</button>
          ) : null}
          <button type="button" onClick={() => question && practice.play(question)} disabled={!question}>↻ Replay</button>
        </div>
        <div className="answer-grid" role="group" aria-label="Answers">
          {choices.map((c) => {
            const isAnswer = result && c.id === result.answer.id;
            const isWrongPick = result && !result.correct && c.id === result.given;
            return (
              <button key={c.id} type="button" disabled={!awaiting}
                className={`answer ${isAnswer ? "correct" : ""} ${isWrongPick ? "wrong" : ""}`}
                onClick={() => void answer(c.id)}>
                {c.label}
              </button>
            );
          })}
        </div>
        <div aria-live="assertive" className="ear-feedback">
          {result && (
            <p className={result.correct ? "status ok" : "status warn"}>
              {result.correct ? "Correct! " : `Not quite — it was ${result.answer.label}. `}
              {result.reveal}
              {result.levelChange > 0 && " Level up!"}
              {result.levelChange < 0 && " Moving down a level to consolidate."}
            </p>
          )}
        </div>
      </div>
    </Panel>
  );
}
